/**
 * Isolated auth for embedded (iframe) usage — e.g. the content platform's
 * Super AI assistant page (`/web/superagent/:code`).
 *
 * Problem: the embedded iframe and the standalone agent platform share the
 * same origin and therefore the same `localStorage`. Logging out of the
 * standalone platform runs `localStorage.clear()` (see request.ts 401
 * handling), which also wipes the iframe's token — the embed "logs out" too.
 *
 * Fix: in embedded mode keep the token in `sessionStorage`, which is scoped
 * to THIS iframe's browsing context. A logout in the standalone tab cannot
 * touch it, so the embedded session (driven by the content platform token
 * delivered via postMessage) stays independent.
 */

const EMBED_TOKEN_KEY = 'embedToken';
const EMBED_MODE_KEY = 'embedMode';

// Mark the current context as an embedded iframe session.
export const markEmbedded = (): void => {
  try {
    sessionStorage.setItem(EMBED_MODE_KEY, '1');
  } catch {
    /* ignore storage errors (private mode etc.) */
  }
};

// Whether we are running as an embedded iframe session.
export const isEmbedded = (): boolean => {
  try {
    return sessionStorage.getItem(EMBED_MODE_KEY) === '1';
  } catch {
    return false;
  }
};

// Persist the token delivered by the embedder (content platform) into the
// isolated sessionStorage store and flag embedded mode.
export const setEmbeddedToken = (token: string): void => {
  try {
    sessionStorage.setItem(EMBED_MODE_KEY, '1');
    if (token) sessionStorage.setItem(EMBED_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
};

export const getEmbeddedToken = (): string => {
  try {
    return sessionStorage.getItem(EMBED_TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Unified auth-token accessor. All token readers (axios interceptor, SSE
 * stream, interrupt call) MUST go through this.
 *
 * - Embedded iframe: STRICTLY use the token delivered by the content platform
 *   via postMessage (kept in the isolated sessionStorage store). We never fall
 *   back to ai-chat-web's own `localStorage` login, so the embed fully mirrors
 *   the content platform identity even though both share the same JWT system.
 *   A stale ai-chat standalone login can therefore never leak into the embed.
 * - Standalone: use the normal `localStorage` token.
 */
export const getAuthToken = (): string => {
  if (isEmbedded()) {
    return getEmbeddedToken();
  }
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
};

// Ask the parent (embedder) for a fresh token. Used on mount and when an
// embedded request hits 401, instead of clearing storage / redirecting to
// the standalone login page.
export const requestParentToken = (): void => {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'REQUEST_AUTH_TOKEN' }, '*');
    }
  } catch {
    /* ignore */
  }
};
